<template>
    <div class="!my-0 mx-24 py-24">
        <div class="flex justify-end">
            <simple-button
                :disabled="!hasMultiPostingSubscription"
                display="secondary"
                icon="add-circle"
                :loading="creatingContract"
                look="primary"
                @click="creatingContract = true"
            >
                {{ $t('multi-posting.tabs.contracts.add') }}
            </simple-button>
        </div>
        <div class="border-t w-full my-24"></div>
        <div>
            <div class="mb-24">
                <p class="text-heading-2 font-semibold">{{ $t('multi-posting.tabs.contracts.title') }}</p>
            </div>
            <div>
                <ContractList
                    ref="contractList"
                    :has-multi-posting-subscription="hasMultiPostingSubscription"
                    :creating.sync="creatingContract"
                    @change-tab="$emit('change-tab', {tab: 'Campaigns', params: 'create'})"
                />
            </div>
        </div>

        <popup-modal v-if="creatingContract" custom-classes="w-768" :text-center="false" @close="stopCreating">
            <div>
                <div>
                    <p class="text-heading-2 font-semibold mb-4">{{ $t('multi-posting.contracts.add_contract.title') }}</p>
                    <p>{{ $t('multi-posting.contracts.add_contract.description') }}</p>
                </div>
                <div class="border-t mt-24 mb-16"></div>
                <div>
                    <select-dropdown
                        :autoTranslate="false"
                        enable-when-no-options
                        keep-search-after-selection
                        :loading="fetching"
                        :options="contractsAsOptions"
                        :placeholder="$t('multi-posting.contracts.add_contract.select.placeholder')"
                        remove-icon
                        searchable
                        v-model="contractId"
                        @search="search($event)"
                        @removedSelection="contractTypes = []"
                    >
                        {{ $t('multi-posting.contracts.add_contract.select.label') }}
                    </select-dropdown>
                </div>
                <div v-if="contract" class="mt-16">
                    <div class="flex items-center">
                        <div class="w-64 h-64">
                            <img :src="contract.logo_url" :alt="`${contract.name}-logo`">
                        </div>
                        <div class="flex flex-col justify-center ml-16">
                            <p class="text-heading-1 font-semibold mb-4">{{ contract.name }}</p>
                            <a :href="contract.url">{{ contract.url }}</a>
                        </div>
                    </div>
                    <p>{{ contract.description }}</p>
                    <div class="contract-setup">
                        <p  v-html="contract.setup_instructions"></p>
                    </div>
                </div>
                <div v-if="contract" class="border-t mt-24 mb-16"></div>
            </div>
            <div v-if="form && contract">
                <multi-posting-dynamic-contract-form
                    :form="form"
                    :contract="contract"
                    :authDetails="authDetails"
                    :disabled="authRequired"
                    @validation="handleFormValidation"
                    @formChanged="formChanged"
                />
                <multi-posting-group-selection
                    :form="form"
                    :contract="contract"
                    :disabled="authRequired"
                    @group-selected="handleGroupSelection"
                />
                <div class="grid grid-cols-2 gap-16 mb-20 mt-20">
                    <div class="col-span-2">
                        <p class="block cursor-default mb-8 first-letter:uppercase font-semibold text-black">
                            {{ $t('multi-posting.posting_duration_days') }}
                        </p>
                        <div class="flex justify-between items-center">
                            <p class="mr-8">7</p>
                            <range-slider
                                v-model="optionalForm.data.postingDurationDays"
                                :disabled="authRequired"
                                :min="7"
                                :max="365"
                                :step="1"
                            >
                                <template #tooltip>
                                    {{ optionalForm.data.postingDurationDays + " " + $tc('general.days', optionalForm.data.postingDurationDays) }}
                                </template>
                            </range-slider>
                            <p class="ml-8">365</p>
                        </div>
                    </div>
                    <div class="col-span-2">
                        <a @click="showOptionalDetails = !showOptionalDetails" class="show-hide-details">
                            <span v-if="!showOptionalDetails">{{ $t('multi-posting.contracts.show_optional_details') }}</span>
                            <span v-else>{{ $t('multi-posting.contracts.hide_optional_details') }}</span>
                        </a>
                    </div>
                </div>
                <multi-posting-optional-contract-form
                    v-if="showOptionalDetails"
                    :form="optionalForm"
                    :disabled="authRequired"
                    @formChanged="optionalFormChanged"
                />
                <div class="border-t my-16"></div>
                <div>
                    <checkbox
                        v-model="approved"
                        :disabled="authRequired">
                        {{ $t('multi-posting.contracts.add_contract.terms_and_conditions') }}
                    </checkbox>
                    <div class="flex justify-end">
                        <simple-button
                            class="mt-24"
                            :disabled="!valid || authRequired"
                            display="secondary"
                            :loading="submittingContract"
                            look="primary"
                            @click="createContract"
                        >
                            {{ $t('multi-posting.tabs.contracts.add') }}
                        </simple-button>
                    </div>
                </div>
            </div>
        </popup-modal>
    </div>
</template>

<script>
import MultiPostingEmptyListItem from "../components/multiposting/MultiPostingEmptyListItem.vue";
import SimpleButton from "../components/ui/SimpleButton.vue";
import PaidSubscription from "../models/PaidSubscription";
import PopupModal from "../components/ui/PopupModal.vue";
import Loading from "../components/ui/Loading.vue";
import MultiPostingDynamicContractForm from "../components/multiposting/MultiPostingDynamicContractForm.vue";
import MultiPostingGroupSelection from "../components/multiposting/MultiPostingGroupSelection.vue";
import SelectDropdown from "../components/forms/SelectDropdown.vue";
import Form from "../etc/forms/Form";
import * as Vonq from "/../../vendor/jobtoolz/vonq/public/js/share/Vonq.js"
import { ADD_NOTIFICATION } from "../state/mutation-types";
import {mapState, mapActions} from "pinia";
import {useMainStore} from '../state/store';
import {useMultiPostingStore} from '../state/modules/multiPosting';
import {useNotificationsStore} from '../state/modules/notifications';
import ContractList from "../components/multiposting/ContractList.vue";
import Checkbox from "../components/forms/Checkbox.vue";
import MultiPostingOptionalContractForm from "../components/multiposting/MultiPostingOptionalContractForm.vue";
import RangeSlider from "../components/ui/RangeSlider.vue";
import {useRepo} from "pinia-orm";

export default {
    name: 'Contracts',
    components: {
        MultiPostingOptionalContractForm,
        Checkbox,
        ContractList,
        SelectDropdown,
        MultiPostingDynamicContractForm,
        MultiPostingGroupSelection,
        Loading,
        PopupModal,
        MultiPostingEmptyListItem,
        SimpleButton,
        RangeSlider
    },
    data() {
        return {
            approved: false,
            creatingContract: false,
            contractId: null,
            contract: undefined,
            form: null,
            optionalForm: new Form([
                { name: 'contractAlias', value: null },
                { name: 'contractExpiry', value: null },
                { name: 'purchasePrice', value: null },
                { name: 'credits', value: null },
                { name: 'currency', value: null },
                { name: 'postingDurationDays', value: 7 },
            ]),
            fetching: false,
            contractTypes: [],
            searchTimeout: null,
            formIsValid: false,
            errors: {},
            showErrors: false,
            submittingContract: false,
            showOptionalDetails: false,
            group: null,
            updateGroups: false,
            authRequired: false,
            authDetails: {
                fieldName: "",
                hash: ""
            }
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        ...mapState(useMultiPostingStore, ['contractAuthState']),
        contractsAsOptions() {
            return this.contractTypes.map(contract => {
                return {
                    value: contract.id,
                    label: contract.name,
                    img: contract.logo_url,
                }
            })
        },
        hasMultiPostingSubscription() {
            return !!this.multiPostingSubscription && this.currentCompany.multiposting_status === 'active';
        },
        multiPostingSubscription() {
            return useRepo(PaidSubscription)
                .where('type', 'multi-posting')
                .where('company_id', this.currentCompany.id)
                .where('active', true)
                .first();
        },
        valid() {
            return this.formIsValid && this.approved;
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        ...mapActions(useMultiPostingStore, ['fetchContractGroups', 'clearContractAuthState']),
        generateForm() {
            if (!this.contractId) {
                this.contract = null;
                return new Form([]);
            }

            let formData = [];

            Vonq.api({
                "action": 'getProductSupportingContracts',
                "channelId": String(this.contractId),
                userId: this.currentCompany.id
            })
                .then((contract) => {
                    contract.contract_credentials?.forEach((credential) => {
                        formData.push({ name: credential.name, value: null, readonly: false });
                    });
                    this.contract = contract;

                    if (this.authDetails?.valid && this.authDetails.contractId !== contract.id) {
                        this.authDetails = {}
                    }
                    const oauthCredential = this.contract.contract_credentials?.find(cred => (!!cred.url && cred.name != 'feed-random'))
                    const hasHash = this.authDetails?.valid ? this.authDetails.hash.length > 0 : false
                    this.authRequired = oauthCredential && !hasHash
                })
                .then(() => this.form = new Form(formData));
        },
        search(value) {
            this.fetching = true;

            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            this.searchTimeout = setTimeout(() => {
                this.fetchContracts(value);
                clearTimeout(this.searchTimeout);
            }, 250);
        },
        fetchContracts(search) {
            const SEARCH_SIZE = 3;
            this.fetching = true;

            if (search === '' || search.length < SEARCH_SIZE) {
                this.contractTypes = [];
                this.fetching = false;
                return;
            }

            Vonq.api({
                action: 'getProductsSupportingContracts',
                input: search,
                userId: this.currentCompany.id
            })
            .then((result) => this.contractTypes = result )
            .finally(() => this.fetching = false);
        },
        createContract() {
            this.submittingContract = true
            this.form.submit(data => {
                const request = {
                    action: 'createContract',
                    credentials: data,
                    optional: this.optionalForm.data,
                    channelId: this.contract.id,
                    userId: this.currentCompany.id,
                    followedInstructions: this.form['followed_instructions'],
                    postingRequirements: this.contract.postingRequirements,
                    group: this.group,
                };

                return Vonq.api(request)
                    .then(() => this.approved = false)
                    .then(() => this.submittingContract = false)
                    .then(() => this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('multi-posting.contracts.add_contract.success'),
                    }))
                    .catch((result) => {
                        if (result && result.response && result.response.data && result.response.data.errors) {
                            this[ADD_NOTIFICATION]({
                                type: 'error',
                                message: result.response.data.errors.message,
                            });
                        }
                    })
                    .finally(() => {
                        this.submittingContract = false;
                        this.fetching = false
                    });
            }).then(() => {
                this.$refs.contractList.getContracts();
                this.stopCreating();
            });
        },
        stopCreating() {
            this.approved = false;
            this.contractId = null;
            this.contract = null;
            this.contractTypes = [];
            this.creatingContract = false;
            this.group = null;
            this.resetOptionalForm();
            this.authDetails = {};
            if (this.updateGroups) {
                this.fetchContractGroups();
            }
        },
        resetOptionalForm() {
            this.optionalForm.reset('contractAlias', null);
            this.optionalForm.reset('contractExpiry', null);
            this.optionalForm.reset('purchasePrice', null);
            this.optionalForm.reset('credits', null);
            this.optionalForm.reset('currency', null);
            this.optionalForm.reset('postingDurationDays', null);
        },
        handleFormValidation(isValid, errors) {
            this.formIsValid = isValid;
            this.errors = errors;
            if (this.showErrors) {
                this.form.setErrors(this.errors)
            }
        },
        resetErrors() {
            this.showErrors = false;
            this.errors = {};
            if (this.form) {
                this.form.resetErrors();
            }
        },
        handleGroupSelection({ group, isNew }) {
            this.group = group;
            if (isNew) {
                this.updateGroups = true;
            }
        },
        formChanged(data) {
            this.form[data.field] = data.val;
        },
        optionalFormChanged(data) {
            this.optionalForm[data.field] = data.val;
        }
    },
    beforeMount() {
        this.fetchContractGroups();
    },
    mounted() {
        // when state exists, try opening the new contract popup and pre-load
        if (this.contractAuthState?.valid) {
            this.authDetails['contractId'] = this.contractAuthState.contractId
            this.authDetails['fieldName'] = this.contractAuthState.fieldName
            this.authDetails['hash'] = this.contractAuthState.hash
            this.authDetails['valid'] = true

            this.contractId = this.contractAuthState.contractId
            this.creatingContract = true
            this.clearContractAuthState()
        }
    },
    watch: {
        'contractId'() {
            this.generateForm();
            this.resetErrors();
            this.approved = false;
        },
        'approved'() {
            if (this.approved) {
                this.showErrors = true;
                this.form.setErrors(this.errors)
            }
        }
    },
}
</script>
<style scoped>
    .contract-setup {
        padding-top: 5px;
    }
    .show-hide-details {
        cursor: pointer;
        color: #334076;
        text-decoration: underline;
        font-weight: 600;
    }
</style>
